<template lang="pug">
.twin-relations-box(:style="{ maxWidth: props.maxWidth }")
  .twin-relations-header.flex.items-center.justify-between.mb-1
    .smx-label {{ $t("relations") }}
    el-popover(:visible="popoverVisible", :width="300", trigger="click")
      template(#reference)
        el-button(
          v-if="!disabled",
          text,
          icon="Edit",
          @click="() => (popoverVisible = disabled ? false : !popoverVisible)"
        ) {{ $t("edit") }}
      //- NOTE: we use popoverVisible here to trigger visibility to reduce loading according to https://github.com/inctec/smx-portal/issues/203
      TwinSelect(
        v-if="popoverVisible",
        v-model="relations",
        v-loading="relationsLoading",
        multiple,
        :query="query"
      )
      .flex.justify-end.mt-4
        el-button(type="primary", @click="popoverVisible = false") {{ $t("close") }}

  .twin-references.bg-white.rounded-lg.p-4
    .placeholder(v-if="relations.length === 0")
      span.text-slate-400 {{ $t("noRelations") }}
    template(v-else)
      router-link.text-accent.mb-4.truncate(
        v-for="t in relations",
        :key="t.uuid",
        :title="t.name",
        :to="{ name: appStore.getLink(`twin-edit.${t.type}.${t.templateUuid}.${t.uuid}`).routeName, params: { template: t.templateUuid, id: t.uuid } }"
      ) {{ t.name }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import type { TwinResponse } from "../../../services/twinManagement";
import { useAppStore } from "../../../stores/apps";
import TwinSelect from "./TwinSelect.vue";
import { useTwinStore } from "../../../stores/twinManagement";

const props = defineProps<{
  twin: TwinResponse;
  query: object;
  disabled?: boolean;
  maxWidth?: number | string;
}>();

const appStore = useAppStore();

const popoverVisible = ref(false);

const relationsLoading = computed(
  () => twinManagementStore.relationsLoadingState.loading,
);

const relations = computed({
  get() {
    return twinManagementStore.relationsLoadingState.result
      .filter((relation) => relation.attributes?.propertyUuid === undefined)
      .map((item) =>
        twinManagementStore.relationsLoadingState.twins.find(
          (t) => item.toUuid === t.uuid,
        ),
      );
  },
  set(val: TwinResponse[]) {
    const added = val.filter(
      (item) => !relations.value.some((rel) => rel.uuid === item.uuid),
    );
    const removed = relations.value.filter(
      (item) => !val.some((rel) => rel.uuid === item.uuid),
    );

    if (added.length > 0) {
      added.forEach((rel) => {
        twinManagementStore.addTwinRelation({
          fromUuid: props.twin.uuid,
          toUuid: rel.uuid,
          contextId: "",
          attributes: {},
        });
      });
    }
    if (removed.length > 0) {
      removed.forEach((removedItem) => {
        // Find the relation
        const relation = twinManagementStore.relationsLoadingState.result.find(
          (item) => item.toUuid === removedItem.uuid,
        );
        if (relation) {
          twinManagementStore.removeTwinRelation(relation.uuid);
        }
      });
    }
  },
});

const twinManagementStore = useTwinStore();

onMounted(() => {
  twinManagementStore.loadTwinRelations({
    page: 1,
    pageSize: 100,
    twinUuid: props.twin.uuid,
  });
});
</script>

<style scoped>
.twin-references {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
